import { Box, Card, CardBody, FormControl, FormLabel, Heading, Input, SimpleGrid, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ContributingEntityRole, type EspdResponseDto } from "../../../../../autogen/bff-api";
import { LabeledValue } from "../../../../../common/components/LabeledValue";
import { YesNoQuestionCard } from "../../../../../common/components/YesNoQuestionCard";
import type { ContributionAudience } from "../ContributionWizard";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  name: string;
  organizationNumber: string;
  role: ContributingEntityRole;
  groupRole?: string | null;
  groupName?: string | null;
  otherParticipants?: string[];
  espd: EspdResponseDto;
  onChange: (next: EspdResponseDto) => void;
  readOnly: boolean;
  audience: ContributionAudience;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const ContributionSupplierStep = ({
  name,
  organizationNumber,
  role,
  groupRole,
  groupName,
  otherParticipants,
  espd,
  onChange,
  readOnly,
  audience,
  previousStep,
  nextStep,
  nextStepLabel,
}: Props) => {
  const { t } = useTranslation();

  const isConsortiumMember = role === ContributingEntityRole.ConsortiumMember;
  const participants = otherParticipants ?? [];

  const eo = espd.economicOperator ?? { smeIndicator: null, representatives: [] };
  const rep = eo.representatives?.[0] ?? { firstName: "", familyName: "" };

  const setRep = (patch: Partial<typeof rep>) =>
    onChange({ ...espd, economicOperator: { ...eo, representatives: [{ ...rep, ...patch }] } });

  return (
    <Box pb="8" pt="6">
      <Heading size="md" mb="6">
        {t("About the supplier")}
      </Heading>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="4">
            {t("Identification")}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="8" spacingY="4">
            <LabeledValue label={t("Company name")} value={name} />
            <LabeledValue label={t("Organization number")} value={organizationNumber} />
            {groupRole && (
              <LabeledValue
                label={isConsortiumMember ? t("Role in the group") : t("Part of the delivery")}
                value={groupRole}
              />
            )}
            {isConsortiumMember && groupName && <LabeledValue label={t("The group")} value={groupName} />}
            {isConsortiumMember && participants.length > 0 && (
              <LabeledValue label={t("Other participants")} value={participants.join(", ")} />
            )}
          </SimpleGrid>
          {audience === "contributor" && (groupRole || participants.length > 0) && (
            <Text fontSize="xs" color="smMuted" mt="4">
              {t("Stated by the lead supplier. Contact them if anything is wrong.")}
            </Text>
          )}
        </CardBody>
      </Card>

      <Card variant="outline" rounded="xl" mb="6">
        <CardBody>
          <Heading size="sm" mb="1">
            {t("Authorized representative")}
          </Heading>
          <Text fontSize="sm" color="smMuted" mb="4">
            {t("The person who answers on behalf of the company.")}
          </Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX="6" spacingY="4">
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("First name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.firstName}
                onChange={(e) => setRep({ firstName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Family name")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.familyName}
                onChange={(e) => setRep({ familyName: e.target.value })}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{t("Email")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                type="email"
                value={rep.email ?? ""}
                onChange={(e) => setRep({ email: e.target.value })}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">{t("Role")}</FormLabel>
              <Input
                isReadOnly={readOnly}
                value={rep.role ?? ""}
                onChange={(e) => setRep({ role: e.target.value })}
              />
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      <Stack spacing="5" mb="8">
        <YesNoQuestionCard
          text={t("Is the supplier a micro, small or medium-sized enterprise (SME)?")}
          value={eo.smeIndicator ?? null}
          onChange={(yes) => onChange({ ...espd, economicOperator: { ...eo, smeIndicator: yes } })}
          readOnly={readOnly}
        />
      </Stack>

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
